import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { serviceRequestsAPI } from '@/lib/api';
import Navbar from '@/components/Navbar';
import { FileText, Clock, Shield, CheckCircle, ArrowRight } from 'lucide-react';
import { trackServiceSelection } from '@/utils/analytics';

const ITRServicesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedServiceName, setSelectedServiceName] = useState(null);

  const itrServices = [
    {
      name: 'ITR-1 (Sahaj) - Self Assisted',
      price: 999,
      suitableFor: 'Salaried Individuals',
      description: 'Basic ITR-1 filing for salaried individuals with simple tax returns.',
      features: [
        'Return Preparation & Filing',
        'Basic Income Verification',
        'E-filing Acknowledgement',
        'Income from salary/pension',
        'One house property'
      ],
      documents: ['Form 16', 'Bank Statements', 'PAN Card', 'Aadhaar Card']
    },
    {
      name: 'ITR-1 Premium Care',
      price: 2449,
      suitableFor: 'Salaried Individuals',
      description: 'Premium ITR-1 filing with expert assistance and complete support.',
      features: [
        'Return Preparation & Filing',
        'Expert Assistance Throughout',
        'Tax Saving Review',
        'Defective Return Support',
        'Income Tax Notice Support',
        'Post-Filing Assistance',
        'Total income up to ₹50 lakhs'
      ],
      documents: ['Form 16', 'Bank Statements', 'PAN Card', 'Aadhaar Card'],
      premium: true
    },
    {
      name: 'ITR-2',
      price: 1999,
      suitableFor: 'Individuals with Capital Gains',
      description: 'For individuals and HUFs with capital gains, multiple properties, or foreign income.',
      features: [
        'Capital gains (stocks, property)',
        'Multiple house properties',
        'Foreign income/assets',
        'Director in a company'
      ],
      documents: ['Form 16', 'Capital Gains Statement', 'Property Documents', 'Foreign Asset Details']
    },
    {
      name: 'ITR-3',
      price: 3999,
      suitableFor: 'Business Owners & Professionals',
      description: 'For individuals/HUFs with income from business or profession.',
      features: [
        'Business/profession income',
        'Partnership firm income',
        'Presumptive income u/s 44AD/44ADA',
        'Detailed P&L and Balance Sheet'
      ],
      documents: ['Books of Accounts', 'P&L Statement', 'Balance Sheet', 'GST Returns', 'Bank Statements']
    },
    {
      name: 'ITR-4 (Sugam)',
      price: 1499,
      suitableFor: 'Small Business Owners',
      description: 'For presumptive income from business/profession under sections 44AD, 44ADA, or 44AE.',
      features: [
        'Presumptive taxation scheme',
        'Turnover up to ₹2 crore',
        'Simplified compliance',
        'Lower documentation'
      ],
      documents: ['Turnover Details', 'Bank Statements', 'PAN Card', 'Business Registration']
    }
  ];

  const processSteps = [
    { step: 1, title: 'Select Service', desc: 'Choose the right ITR form' },
    { step: 2, title: 'Upload Documents', desc: 'Share required documents' },
    { step: 3, title: 'CA Review', desc: 'Expert verification' },
    { step: 4, title: 'File Return', desc: 'E-file with acknowledgment' }
  ];

  const handleSelectService = async (serviceName) => {
    // If not logged in, redirect to login
    if (!user) {
      navigate('/login');
      return;
    }

    setLoading(true);
    setSelectedServiceName(serviceName);

    try {
      // Create a service request with the service name
      // Since these are frontend-only services, we'll match with backend services by name
      const response = await serviceRequestsAPI.create({ 
        service_name: serviceName,
        category: 'income_tax'
      });
      
      // Find the service price
      const service = itrServices.find(s => s.name === serviceName);
      const servicePrice = service?.price || 0;
      
      // Track service selection conversion
      trackServiceSelection(serviceName, servicePrice);
      
      // Show success and redirect to dashboard
      setTimeout(() => {
        navigate('/dashboard');
      }, 1000);
    } catch (err) {
      console.error('Error creating service request:', err);
      alert('Failed to select service. Please try again.');
      setLoading(false);
      setSelectedServiceName(null);
    }
  };

  const handleGetStarted = () => {
    navigate('/services');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-20" data-testid="itr-hero">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-5xl font-bold mb-6" data-testid="itr-title">Income Tax Return (ITR) Filing</h1>
          <p className="text-xl text-blue-100 max-w-3xl mb-8">
            Expert CA-assisted ITR filing services for all types of taxpayers. Quick, accurate, and hassle-free.
          </p>
          <button 
            onClick={handleGetStarted}
            className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-50 transition inline-flex items-center gap-2"
            data-testid="start-filing-button"
          >
            Start Filing Now <ArrowRight size={20} />
          </button>
        </div>
      </section>

      {/* Why File ITR */}
      <section className="py-16 px-4" data-testid="why-file-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Why File ITR with ITRQUICK?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-sm" data-testid="benefit-expert-review">
              <Shield className="text-blue-600 mb-4" size={40} />
              <h3 className="text-xl font-semibold mb-3">Expert CA Review</h3>
              <p className="text-gray-600">Every return is reviewed by qualified Chartered Accountants to ensure accuracy and compliance.</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm" data-testid="benefit-quick-process">
              <Clock className="text-blue-600 mb-4" size={40} />
              <h3 className="text-xl font-semibold mb-3">Quick Processing</h3>
              <p className="text-gray-600">Get your ITR filed within 24-48 hours with proper documentation and verification.</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm" data-testid="benefit-secure">
              <FileText className="text-blue-600 mb-4" size={40} />
              <h3 className="text-xl font-semibold mb-3">Secure & Compliant</h3>
              <p className="text-gray-600">Your data is encrypted and stored securely. We follow all IT department guidelines.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ITR Services */}
      <section className="py-16 px-4 bg-white" data-testid="itr-services-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">Our ITR Filing Services</h2>
          <p className="text-center text-gray-600 mb-12">Choose the right ITR form based on your income type</p>
          
          <div className="grid md:grid-cols-2 gap-8">
            {itrServices.map((service) => (
              <div key={service.name} className={`rounded-xl p-6 border-2 transition ${service.premium ? 'bg-gradient-to-br from-purple-50 to-blue-50 border-purple-300 shadow-lg' : 'bg-gray-50 border-gray-200 hover:border-blue-400'}`} data-testid={`itr-service-${service.name.toLowerCase()}`}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-2xl font-bold">{service.name}</h3>
                      {service.premium && (
                        <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs font-bold px-2 py-1 rounded">PREMIUM</span>
                      )}
                    </div>
                    <p className="text-blue-600 font-medium">{service.suitableFor}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-gray-900">₹{service.price}</div>
                  </div>
                </div>
                
                <p className="text-gray-700 mb-4">{service.description}</p>
                
                <div className="mb-4">
                  <p className="font-semibold mb-2">Key Features:</p>
                  <ul className="space-y-2">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="mb-4">
                  <p className="font-semibold mb-2">Required Documents:</p>
                  <div className="flex flex-wrap gap-2">
                    {service.documents.map((doc) => (
                      <span key={doc} className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full">{doc}</span>
                    ))}
                  </div>
                </div>
                
                <button
                  onClick={() => handleSelectService(service.name)}
                  disabled={loading && selectedServiceName === service.name}
                  className={`w-full py-3 rounded-lg font-semibold transition ${
                    user 
                      ? 'bg-blue-600 text-white hover:bg-blue-700' 
                      : 'bg-gray-300 text-gray-600 cursor-pointer hover:bg-gray-400'
                  }`}
                  data-testid={`select-${service.name.toLowerCase()}`}
                >
                  {loading && selectedServiceName === service.name ? (
                    'Adding...'
                  ) : user ? (
                    'Select This Service'
                  ) : (
                    'Login to Select Service'
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 px-4" data-testid="process-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">How It Works</h2>
          <div className="grid md:grid-cols-4 gap-8">
            {processSteps.map((item) => (
              <div key={item.step} className="text-center" data-testid={`step-${item.step}`}>
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 text-white rounded-full text-2xl font-bold mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-blue-600 text-white" data-testid="final-cta">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to File Your ITR?</h2>
          <p className="text-xl text-blue-100 mb-8">Get started in minutes with expert CA assistance</p>
          <button
            onClick={handleGetStarted}
            className="bg-white text-blue-600 px-12 py-4 rounded-lg text-xl font-semibold hover:bg-blue-50 transition"
            data-testid="final-cta-button"
          >
            File Your ITR Now
          </button>
        </div>
      </section>
    </div>
  );
};

export default ITRServicesPage;